import { Component, ErrorInfo, ReactNode } from 'react';
import { reportReactError } from '@/utils/error-reporter';

import FaIcon from './FaIcon';

/** Must match OTA_STATE_KEY_PREFIX in services/live-update.service.ts. */
const LIVE_UPDATE_STATE_PREFIX = 'cmdbridge.ota.';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * Values that clear a caught error when any of them changes.
   *
   * A boundary latches: once it catches, it renders the fallback forever, because
   * nothing in React clears that state. For an app-level boundary that is right —
   * the page is broken and the only honest offer is a reload. For a boundary
   * around routed content it is not: the operator navigates somewhere else, the
   * new page is fine, and they still stare at the error from the last one.
   *
   * Pass the values that mean "this is different content now" — a pathname, a
   * record id. Omit it and the boundary latches exactly as before, so no existing
   * caller changes behaviour.
   */
  resetKeys?: unknown[];
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    this.setState({ error, errorInfo });

    // Report to error tracking system
    reportReactError(error, errorInfo.componentStack || undefined);
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.state.hasError) return;
    const prev = prevProps.resetKeys;
    const next = this.props.resetKeys;
    // Both absent means the caller never opted in; latch as before.
    if (!prev || !next || prev.length !== next.length) return;
    if (prev.some((key, i) => !Object.is(key, next[i]))) {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  }

  handleReload = () => {
    // Clear caches and reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload();
  };

  handleClearAndReload = async () => {
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
    // Clear caches
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map(name => caches.delete(name)));
    }
    // Clear local storage -- except the native app's live-update state (its
    // downloaded-bundle and failed-version markers and anti-rollback counters,
    // OTA_STATE_KEY_PREFIX in services/live-update.service.ts). Wiping it would
    // orphan a downloaded bundle and reset the device's rollback protection.
    const keep: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      const value = key?.startsWith(LIVE_UPDATE_STATE_PREFIX) ? localStorage.getItem(key) : null;
      if (key && value !== null) keep.push([key, value]);
    }
    localStorage.clear();
    for (const [key, value] of keep) localStorage.setItem(key, value);
    sessionStorage.clear();
    // Reload
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-surface-elevated rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <FaIcon icon="triangle-exclamation" className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-xl font-semibold text-theme-primary">
                Something went wrong
              </h1>
            </div>

            <p className="text-theme-secondary mb-4">
              An error occurred while loading this page. This might be due to a cached version conflict.
            </p>

            {this.state.error && (
              <div className="mb-4 p-3 bg-surface-tertiary rounded text-sm font-mono text-red-600 dark:text-red-400 overflow-auto max-h-32">
                {this.state.error.message}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <button
                onClick={this.handleReload}
                className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleClearAndReload}
                className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-theme-secondary rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Clear Cache & Reload
              </button>
            </div>

            {this.state.errorInfo && (
              <details className="mt-4">
                <summary className="text-sm text-theme-tertiary cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                  Technical Details
                </summary>
                <pre className="mt-2 p-2 bg-surface-tertiary rounded text-xs overflow-auto max-h-48 text-theme-secondary">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
